import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { signUp } from "aws-amplify/auth";
import styles from "./MantineSignUpPage.module.css";

import {
  TextInput,
  Button,
  Text,
  Stack,
  Checkbox,
  LoadingOverlay,
  Box,
  Group,
  Title,
} from "@mantine/core";
import PasswordRequirementInput from "./common/PasswordRequirementInput";
import StandardizedPhoneInput from "./common/StandardizedPhoneInput";
import { isPasswordValid } from "../utils/passwordValidation";

function MantineSignUpPage({ onPageChange }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    phoneRaw: "", // E.164 format for Cognito
    password: "",
    acceptTerms: false,
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  // Check URL parameters for email (from invitation links)
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const emailParam = searchParams.get("email");
    if (emailParam) {
      // If email is in URL, this is likely an invitation link
      // Redirect to login instead of signup
      navigate("/login", {
        state: {
          email: emailParam,
          message:
            "You already have an account. Please sign in with your temporary password.",
        },
        replace: true,
      });
    }
  }, [location.search, navigate]);

  // Check if form is valid and ready to submit
  const isFormValid = () => {
    return (
      formData.firstName.trim() &&
      formData.lastName.trim() &&
      formData.email.trim() &&
      /\S+@\S+\.\S+/.test(formData.email) &&
      formData.phone.trim() &&
      formData.phone.replace(/\D/g, "").length >= 10 &&
      formData.password.trim() &&
      isPasswordValid(formData.password) &&
      formData.acceptTerms
    );
  };

  const handleInputChange = (name, value) => {
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.firstName.trim())
      newErrors.firstName = "First name is required";
    if (!formData.lastName.trim()) newErrors.lastName = "Last name is required";
    if (!formData.email.trim()) {
      newErrors.email = "Email address is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }
    if (!formData.phone.trim()) {
      newErrors.phone = "Phone number is required";
    } else {
      // Validate phone number has at least 10 digits
      const digitsOnly = formData.phone.replace(/\D/g, "");
      if (digitsOnly.length < 10) {
        newErrors.phone = "Please enter a valid phone number";
      }
    }
    if (!formData.password.trim()) {
      newErrors.password = "Password is required";
    } else if (!isPasswordValid(formData.password)) {
      newErrors.password = "Password is missing required characters";
    }
    if (!formData.acceptTerms) {
      newErrors.acceptTerms = "You must accept the terms and conditions";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle phone number change with raw E.164 format
  const handlePhoneChange = (formattedValue) => {
    setFormData((prev) => ({
      ...prev,
      phone: formattedValue,
    }));
  };

  const handlePhoneRawChange = (rawValue) => {
    // Extract just the 10 digits (no country code prefix) for Cognito
    // Cognito expects E.164 format, but we'll extract digits and format it properly
    const digitsOnly = rawValue.replace(/\D/g, "");
    // Only take the last 10 digits (in case country code was included)
    const tenDigits = digitsOnly.slice(-10);
    // Format as E.164: +1XXXXXXXXXX
    const e164Format = `+1${tenDigits}`;
    setFormData((prev) => ({
      ...prev,
      phoneRaw: e164Format,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsLoading(true);
    try {
      const userAttributes = {
        email: formData.email,
        given_name: formData.firstName,
        family_name: formData.lastName,
        "custom:role": "Owner",
        "custom:tos_accepted": "true",
      };

      // Add phone number (formatted to E.164) - required field
      userAttributes.phone_number = formData.phoneRaw || formData.phone;

      const cognitoResponse = await signUp({
        username: formData.email,
        password: formData.password,
        options: {
          userAttributes,
          autoSignIn: {
            enabled: true,
            authFlowType: "USER_AUTH",
          },
        },
      });

      if (cognitoResponse.nextStep.signUpStep !== "CONFIRM_SIGN_UP") {
        throw new Error("Failed to create user");
      } else {
        navigate("/verify-email", {
          state: { username: formData.email },
        });
      }
    } catch (error) {
      console.error("Error signing up:", error);

      // Check if user already exists (invited user trying to sign up)
      const errorMessage = error.message || "";
      const isUserExistsError =
        errorMessage.includes("already exists") ||
        errorMessage.includes("UsernameExistsException") ||
        errorMessage.includes("An account with the given email already exists");

      if (isUserExistsError) {
        // User already exists - redirect to login
        navigate("/login", {
          state: {
            email: formData.email,
            message:
              "You already have an account. Please sign in with your temporary password.",
          },
          replace: true,
        });
        return;
      }

      setErrors((prev) => ({
        ...prev,
        submit: error.message,
      }));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box className={styles.pageWrapper}>
      {isLoading && (
        <LoadingOverlay visible={true} overlayProps={{ blur: 2 }} />
      )}

      {/* Background blobs */}
      <Box className={styles.backgroundBlobs}>
        <Box className={styles.rightBlob} />
      </Box>

      {/* Header with Logo */}
      <header className={styles.header}>
        <img
          src="/images/pagoda-logo-horizontal.png"
          alt="Pagoda"
          className={styles.logo}
        />
      </header>

      {/* Main Content */}
      <main className={styles.mainContent}>
        {/* Signup Container - wraps title section and form */}
        <Stack gap="lg" className={styles.signupContainer}>
          {/* Title Section */}
          <Stack gap="xs" className={styles.titleSection}>
            <Title order={1} className={styles.title}>
              Let's get started.
            </Title>
            <Text size="sm" c="dimmed" className={styles.subtitle}>
              Already have an account?{" "}
              <Text
                span
                className={styles.subtitleLink}
                onClick={() => onPageChange("login")}
                style={{ cursor: "pointer" }}
              >
                Log-In.
              </Text>
            </Text>
          </Stack>

          {/* Form Card */}
          <Box className={styles.formCard}>
            {errors.submit && (
              <Text className={styles.errorMessage}>{errors.submit}</Text>
            )}

            <form onSubmit={handleSubmit}>
              <Stack className={styles.formStack}>
                {/* Name Row */}
                <Group gap="md" className={styles.nameRow}>
                  <TextInput
                    label="First Name"
                    placeholder="Enter First Name"
                    value={formData.firstName}
                    onChange={(e) =>
                      handleInputChange("firstName", e.target.value)
                    }
                    error={errors.firstName}
                    size="md"
                    radius="sm"
                    required
                    styles={{
                      label: {
                        fontWeight: 500,
                        marginBottom: 8,
                        color: "#101820",
                      },
                    }}
                  />
                  <TextInput
                    label="Last Name"
                    placeholder="Enter Last Name"
                    value={formData.lastName}
                    onChange={(e) =>
                      handleInputChange("lastName", e.target.value)
                    }
                    error={errors.lastName}
                    size="md"
                    radius="sm"
                    required
                    styles={{
                      label: {
                        fontWeight: 500,
                        marginBottom: 8,
                        color: "#101820",
                      },
                    }}
                  />
                </Group>

                <TextInput
                  label="Email"
                  placeholder="Email"
                  value={formData.email}
                  onChange={(e) => handleInputChange("email", e.target.value)}
                  error={errors.email}
                  size="md"
                  radius="sm"
                  required
                  styles={{
                    label: {
                      fontWeight: 500,
                      marginBottom: 8,
                      color: "#101820",
                    },
                  }}
                />

                <StandardizedPhoneInput
                  label="Phone Number"
                  placeholder="(555) 555-5555"
                  value={formData.phone}
                  onChange={handlePhoneChange}
                  onRawChange={handlePhoneRawChange}
                  error={errors.phone}
                  size="md"
                  radius="sm"
                  required
                  styles={{
                    label: {
                      fontWeight: 500,
                      marginBottom: 8,
                      color: "#101820",
                    },
                  }}
                />

                <PasswordRequirementInput
                  label="Password"
                  placeholder="Password"
                  value={formData.password}
                  onChange={(e) =>
                    handleInputChange("password", e.target.value)
                  }
                  error={errors.password}
                  size="md"
                  radius="sm"
                  required
                  showRequirements={true}
                />

                <Checkbox
                  checked={formData.acceptTerms}
                  onChange={(e) =>
                    handleInputChange("acceptTerms", e.target.checked)
                  }
                  error={errors.acceptTerms}
                  className={styles.termsCheckbox}
                  styles={{
                    label: {
                      fontWeight: 500,
                      color: "#101820",
                    },
                    description: {
                      color: "#868e96",
                    },
                  }}
                  label={
                    <Text size="sm">
                      I accept the{" "}
                      <Text
                        component="a"
                        href="https://pagoda.co/terms"
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          color: "#36afc9",
                          textDecoration: "none",
                          fontWeight: 500,
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.textDecoration = "underline";
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.textDecoration = "none";
                        }}
                      >
                        Terms of Service
                      </Text>{" "}
                      and{" "}
                      <Text
                        component="a"
                        href="https://pagoda.co/privacy"
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          color: "#36afc9",
                          textDecoration: "none",
                          fontWeight: 500,
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.textDecoration = "underline";
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.textDecoration = "none";
                        }}
                      >
                        Privacy Policy
                      </Text>
                    </Text>
                  }
                  description="By checking this box, you agree to our terms and conditions"
                />

                <Button
                  type="submit"
                  loading={isLoading}
                  fullWidth
                  size="md"
                  radius="sm"
                  variant="filled"
                  className={styles.submitButton}
                  disabled={!isFormValid()}
                >
                  Register
                </Button>
              </Stack>
            </form>
          </Box>
        </Stack>
      </main>

      {/* Footer */}
      <footer className={styles.footer}>
        <Text size="sm" className={styles.copyright}>
          © Copyright {new Date().getFullYear()}, Pagoda Labs, Inc.
        </Text>
        <Group gap="md" className={styles.footerLinks}>
          <a
            href="https://pagoda.co/privacy"
            target="_blank"
            rel="noopener noreferrer"
            className={styles.footerLink}
          >
            Privacy Policy
          </a>
          <a
            href="https://pagoda.co/terms"
            target="_blank"
            rel="noopener noreferrer"
            className={styles.footerLink}
          >
            Terms & Conditions
          </a>
        </Group>
      </footer>
    </Box>
  );
}

export default MantineSignUpPage;
